import {useTranslation} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
import React, {ErrorInfo, PropsWithChildren, useEffect} from 'react';
import {ErrorPageInline} from '../../atoms/ErrorPageInline/ErrorPageInline';

interface ErrorDetails {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<
  {
    ErrorComponent?: React.ComponentType<{children?: React.ReactNode}> | null;
    renderContext: string;
    children?: React.ReactNode;
  },
  ErrorDetails
> {
  state: ErrorDetails = {
    error: null,
    errorInfo: null,
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({error, errorInfo});
  }

  render() {
    return <ErrorContainer {...this.props} {...this.state} />;
  }
}

interface ErrorContainerProps extends ErrorDetails {
  ErrorComponent?: React.ComponentType<React.PropsWithChildren> | null;
  renderContext: string;
}

function ErrorContainer({
  error,
  errorInfo,
  renderContext,
  children,
  ErrorComponent,
}: PropsWithChildren<ErrorContainerProps>) {
  useEffect(() => {
    if (error) {
      Log.error(
        '@aboutyou/ui-components',
        `Error Boundary rendered Oops component in context ${renderContext}`,
        {
          error,
          stackTrace: errorInfo?.componentStack?.substring(0, 150),
        },
      );
    }
  }, [error, errorInfo, renderContext]);

  if (!error) {
    return children as JSX.Element;
  }

  if (ErrorComponent) {
    return <ErrorComponent />;
  }

  if (ErrorComponent === null) {
    return null;
  }

  return <DefaultErrorComponent error={error} />;
}

const DefaultErrorComponent: React.FC<{error: Error}> = ({error}) => {
  const {t} = useTranslation();

  return (
    <ErrorPageInline
      headline={t('Oops! Something went wrong!')}
      subline={process.env.NODE_ENV !== 'production' && error.message}
      url="/"
    />
  );
};
